import { useEffect, useState, useRef } from "react";
import { T, base } from "../lib/theme";
import { apiFetch } from "../lib/api";
import { Btn, Card, Icon, Toggle, Spinner } from "../components";

const DEFAULT_FILLERS = [
  "ok","okay","k","g","ji","haan","han","ha","acha","achha","accha",
  "theek hai","thk","thek","th","tq","thanks","thankyou","thank you",
  "shukriya","شکریہ","👍","🙏","✅","😊","hmm","hm","oh","ahan","hn",
  "hnji","hy","hey","hi","hello","salam","assalam",".",",","!","?",
];

const DEFAULTS = {
  delayEnabled: true, delayMin: 5, delayMax: 15,
  typingIndicator: true, readReceiptDelay: true,
  batchingEnabled: true, batchWindow: 8,
  rateLimitEnabled: true, rateLimitPerContact: 5, rateLimitGlobal: 30,
  dailyContactLimit: 0, floodGuardSeconds: 3, broadcastDelay: 8,
  fillerDetectionEnabled: true, fillerWords: DEFAULT_FILLERS, fillerMaxWordCount: 0,
};

function calcRisk(s) {
  let score = 0;
  if (!s.delayEnabled) score += 30;
  else if (s.delayMax < 5) score += 25;
  else if (s.delayMax < 8) score += 15;
  if (s.rateLimitEnabled && s.rateLimitPerContact > 10) score += 20;
  else if (s.rateLimitEnabled && s.rateLimitPerContact > 5) score += 10;
  if (!s.typingIndicator) score += 10;
  if (!s.floodGuardSeconds) score += 10;
  if (!s.batchingEnabled || s.batchWindow < 5) score += 10;
  if (!s.fillerDetectionEnabled) score += 5;
  if (s.dailyContactLimit > 0 && s.dailyContactLimit < 10) score += 5;
  if (s.broadcastDelay < 5) score += 15;
  return Math.min(score, 100);
}

// ── Sub-components ─────────────────────────────────────────────────────────

const SliderRow = ({ label, hint, value, min, max, step = 1, onChange, unit = "s", disabled = false }) => {
  const pct = Math.max(0, Math.min(100, ((value - min) / (max - min)) * 100));
  return (
    <div style={{ opacity: disabled ? 0.45 : 1, transition: "opacity 0.2s" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 14, marginBottom: 9 }}>
        <div style={{ minWidth: 0 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: T.text1 }}>{label}</span>
          {hint && <div style={{ fontSize: 11, color: T.text3, marginTop: 2, lineHeight: 1.35 }}>{hint}</div>}
        </div>
        <span style={{
          flexShrink: 0, fontFamily: base.mono, fontSize: 12, fontWeight: 800, color: T.indigo,
          background: T.indigoDim, padding: "4px 9px", borderRadius: 8,
          border: "1px solid rgba(99,102,241,0.22)", minWidth: 54, textAlign: "center",
        }}>
          {value}{unit}
        </span>
      </div>
      <div style={{ position: "relative", height: 6 }}>
        <div style={{ position: "absolute", inset: 0, borderRadius: 3, background: T.border2 }} />
        <div style={{
          position: "absolute", top: 0, left: 0, height: 6, borderRadius: 3,
          background: `linear-gradient(90deg,${T.emerald},${T.sky})`,
          width: `${pct}%`, transition: "width 0.15s",
        }} />
        <input
          type="range" min={min} max={max} step={step} value={value}
          onChange={e => onChange(Number(e.target.value))} disabled={disabled}
          style={{ position: "absolute", top: -5, left: 0, width: "100%", height: 16, opacity: 0, cursor: disabled ? "not-allowed" : "pointer", zIndex: 1 }}
        />
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: T.text3, marginTop: 5 }}>
        <span>{min}{unit}</span><span>{max}{unit}</span>
      </div>
    </div>
  );
};

const ToggleRow = ({ label, hint, value, onChange }) => (
  <div style={{
    display: "flex", alignItems: "center", justifyContent: "space-between",
    gap: 14, padding: "12px 14px", borderRadius: 9,
    background: value ? T.emeraldDim : T.bg0,
    border: `1px solid ${value ? "rgba(99,102,241,0.2)" : T.border1}`,
    transition: "all 0.2s",
  }}>
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: T.text1 }}>{label}</div>
      {hint && <div style={{ fontSize: 11, color: T.text3, marginTop: 2, lineHeight: 1.35 }}>{hint}</div>}
    </div>
    <Toggle value={value} onChange={onChange} />
  </div>
);

const SectionHead = ({ icon, color, title, subtitle, toggle, onToggle }) => (
  <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 14, marginBottom: 18 }}>
    <div style={{ display: "flex", alignItems: "center", gap: 11, minWidth: 0 }}>
      <div style={{
        width: 36, height: 36, borderRadius: 9, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
        background: T[`${color}Dim`], border: `1px solid ${T[color] || "#6366f1"}33`,
      }}>
        <Icon name={icon} size={15} color={T[color] || "#6366f1"} />
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 800, color: T.text1, lineHeight: 1.2 }}>{title}</div>
        {subtitle && <div style={{ fontSize: 11, color: T.text3, marginTop: 3, lineHeight: 1.35 }}>{subtitle}</div>}
      </div>
    </div>
    {toggle !== undefined && <Toggle value={toggle} onChange={onToggle} />}
  </div>
);

const WarnBox = ({ children, level = "rose" }) => (
  <div style={{
    padding: "10px 12px", borderRadius: 8, fontSize: 12, marginTop: 12,
    background: T[`${level}Dim`], color: T[level],
    border: `1px solid ${T[level]}33`, lineHeight: 1.5,
  }}>
    {children}
  </div>
);

const SafetyCard = ({ children, style = {} }) => (
  <Card style={{
    borderRadius: 10,
    padding: 18,
    boxShadow: "none",
    background: T.bg1,
    ...style,
  }}>
    {children}
  </Card>
);

const Stack = ({ children, gap = 16 }) => (
  <div style={{ display: "flex", flexDirection: "column", gap }}>{children}</div>
);

// ── Filler Tag chip component ────────────────────────────────────────────

function FillerChips({ words, onChange, disabled }) {
  const [input, setInput] = useState("");
  const inputRef = useRef(null);

  const addWord = () => {
    const w = input.trim().toLowerCase();
    if (!w) return;
    if (!words.includes(w)) onChange([...words, w]);
    setInput("");
    inputRef.current?.focus();
  };

  const removeWord = (w) => onChange(words.filter(x => x !== w));

  const handleKey = (e) => {
    if (e.key === "Enter" || e.key === ",") { e.preventDefault(); addWord(); }
    if (e.key === "Backspace" && !input && words.length) removeWord(words[words.length - 1]);
  };

  return (
    <div style={{ opacity: disabled ? 0.4 : 1, transition: "opacity 0.2s" }}>
      <div style={{
        display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8, padding: "11px 12px",
        borderRadius: 9, border: `1px solid ${T.border1}`, background: T.bg0,
        minHeight: 58, cursor: disabled ? "not-allowed" : "text",
      }}
        onClick={() => !disabled && inputRef.current?.focus()}
      >
        {words.map(w => (
          <span key={w} style={{
            display: "inline-flex", alignItems: "center", gap: 5,
            minHeight: 28, padding: "5px 10px", borderRadius: 999,
            background: "rgba(99,102,241,0.12)", border: "1px solid rgba(99,102,241,0.25)",
            fontSize: 11, fontWeight: 700, color: "#a5b4fc",
          }}>
            {w}
            {!disabled && (
              <button
                onClick={(e) => { e.stopPropagation(); removeWord(w); }}
                style={{
                  background: "none", border: "none", padding: 0, cursor: "pointer",
                  color: "#7c7ca0", fontSize: 12, lineHeight: 1, display: "flex", alignItems: "center",
                }}
              >×</button>
            )}
          </span>
        ))}
        {!disabled && (
          <input
            ref={inputRef}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKey}
            placeholder={words.length === 0 ? "Type a word and press Enter..." : "Add word..."}
            style={{
              background: "none", border: "none", outline: "none", color: T.text1,
              fontSize: 12, fontFamily: base.font, minWidth: 120, flex: 1, padding: "6px 0",
            }}
          />
        )}
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, marginTop: 7, fontSize: 11, color: T.text3 }}>
        <span>{words.length} words · Press Enter or comma to add</span>
        <button
          disabled={disabled}
          onClick={() => !disabled && onChange(DEFAULT_FILLERS)}
          style={{ background: "none", border: "none", cursor: "pointer", color: T.text3, fontFamily: base.font, fontSize: 11 }}
        >Reset to defaults</button>
      </div>
    </div>
  );
}

// ── Risk score header ────────────────────────────────────────────────────

function RiskBanner({ risk }) {
  const color = risk < 25 ? T.emerald : risk < 55 ? T.amber : T.rose;
  const label = risk < 25 ? "Low risk — well configured" : risk < 55 ? "Medium risk — review warnings" : "High risk — ban likely";
  const desc  = risk < 25
    ? "Your settings look good. WhatsApp is unlikely to flag your number."
    : risk < 55
    ? "Some settings need attention. Check warnings below."
    : "Dangerous — WhatsApp will almost certainly block your number at this rate.";

  const Shield = () => (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
      {risk >= 55 && <><line x1="12" y1="8" x2="12" y2="12"/><circle cx="12" cy="16" r="0.5" fill={color}/></>}
      {risk >= 25 && risk < 55 && <><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></>}
      {risk < 25 && <polyline points="9 12 11 14 15 10"/>}
    </svg>
  );

  return (
    <div style={{
      padding: "16px 18px", borderRadius: 10, marginBottom: 18,
      background: risk < 25 ? T.emeraldDim : risk < 55 ? T.amberDim : T.roseDim,
      border: `1px solid ${color}44`,
      display: "flex", alignItems: "center", gap: 18,
    }}>
      <Shield />
      <div style={{ flex: 1 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <div style={{ fontSize: 14, fontWeight: 800, color }}>{label}</div>
          <div style={{ fontFamily: base.mono, fontSize: 17, fontWeight: 800, color }}>{risk}/100</div>
        </div>
        <div style={{ height: 6, borderRadius: 3, background: "rgba(255,255,255,0.1)", overflow: "hidden" }}>
          <div style={{ height: "100%", width: `${risk}%`, background: color, borderRadius: 3, transition: "width 0.4s ease" }} />
        </div>
        <div style={{ fontSize: 12, color: T.text2, marginTop: 7 }}>{desc}</div>
      </div>
    </div>
  );
}

// ── Main page ────────────────────────────────────────────────────────────

export default function SafetyPage({ token, showToast }) {
  const [s,       setS]       = useState(DEFAULTS);
  const [saving,  setSaving]  = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiFetch("/api/safety-settings", {}, token)
      .then(d => {
        if (d.settings) {
          setS(prev => ({
            ...prev,
            ...d.settings,
            fillerWords: Array.isArray(d.settings.fillerWords) ? d.settings.fillerWords : prev.fillerWords,
            fillerMaxWordCount: typeof d.settings.fillerMaxWordCount === "number"
              ? d.settings.fillerMaxWordCount
              : prev.fillerMaxWordCount,
          }));
        }
      })
      .catch(() => showToast?.("Failed to load settings", "error"))
      .finally(() => setLoading(false));
  }, [token]);

  const set = (key, val) => setS(prev => ({ ...prev, [key]: val }));

  const save = async () => {
    setSaving(true);
    try {
      const result = await apiFetch("/api/safety-settings", {
        method: "PUT",
        body: JSON.stringify(s),
      }, token);
      if (result.success) {
        showToast?.("Safety settings saved — active immediately");
        if (result.settings) {
          setS(prev => ({
            ...prev,
            ...result.settings,
            fillerWords: Array.isArray(result.settings.fillerWords) ? result.settings.fillerWords : prev.fillerWords,
            fillerMaxWordCount: typeof result.settings.fillerMaxWordCount === "number"
              ? result.settings.fillerMaxWordCount
              : prev.fillerMaxWordCount,
          }));
        }
      } else {
        showToast?.(result.error || "Save failed", "error");
      }
    } catch {
      showToast?.("Network error — check connection", "error");
    }
    setSaving(false);
  };

  const resetDefaults = () => { setS(DEFAULTS); showToast?.("Reset to recommended defaults"); };

  const risk      = calcRisk(s);
  const riskColor = risk < 25 ? T.emerald : risk < 55 ? T.amber : T.rose;

  if (loading) return (
    <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <Spinner size={28} />
    </div>
  );

  return (
    <div style={{ flex: 1, overflowY: "auto", padding: "22px 28px 88px" }}>

      <RiskBanner risk={risk} />

      <div style={{
        display: "grid", gridTemplateColumns: "repeat(3,minmax(0,1fr))", gap: 10,
        padding: 12, marginBottom: 18, borderRadius: 10,
        background: T.bg1, border: `1px solid ${T.border1}`,
      }}>
        {[
          ["Dashboard delivery", "Instant", "Incoming messages are shown immediately"],
          ["Batch wait", s.batchingEnabled ? `${s.batchWindow}s` : "Off", "Only combines rapid customer messages"],
          ["Reply delay", s.delayEnabled ? `${s.delayMin}–${s.delayMax}s` : "Off", "Applied after AI prepares the reply"],
        ].map(([label, value, hint]) => (
          <div key={label} style={{ padding: "8px 10px", borderRadius: 8, background: T.bg0 }}>
            <div style={{ fontSize: 10, color: T.text3, textTransform: "uppercase", letterSpacing: ".05em" }}>{label}</div>
            <div style={{ fontSize: 16, fontWeight: 800, color: value === "Instant" ? T.emerald : T.indigo, marginTop: 3 }}>{value}</div>
            <div style={{ fontSize: 10, color: T.text3, marginTop: 2 }}>{hint}</div>
          </div>
        ))}
      </div>

      <div className="grid-2" style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)", gap: 18, alignItems: "start" }}>

        {/* ── LEFT COLUMN ────────────────────────────────────────── */}
        <Stack>

          {/* Reply Delay */}
          <SafetyCard>
            <SectionHead
              icon="clock" color="emerald"
              title="Reply Delay"
              subtitle="Human-like pause before responding"
              toggle={s.delayEnabled} onToggle={v => set("delayEnabled", v)}
            />
            <Stack gap={17}>
              <SliderRow
                label="Minimum delay" hint="Shortest wait before replying"
                value={s.delayMin} min={1} max={30}
                onChange={v => set("delayMin", Math.min(v, s.delayMax))}
                disabled={!s.delayEnabled}
              />
              <SliderRow
                label="Maximum delay" hint="Longest wait before replying"
                value={s.delayMax} min={s.delayMin} max={60}
                onChange={v => set("delayMax", v)}
                disabled={!s.delayEnabled}
              />
            </Stack>
            {!s.delayEnabled && (
              <WarnBox>No delay = instant bot replies. WhatsApp detects this pattern immediately.</WarnBox>
            )}
            {s.delayEnabled && s.delayMax < 5 && (
              <WarnBox level="amber">Under 5s delay is still suspicious. Recommend minimum 8–15 seconds.</WarnBox>
            )}
          </SafetyCard>

          {/* Presence Signals */}
          <SafetyCard>
            <SectionHead
              icon="activity" color="sky"
              title="Presence Signals"
              subtitle="Make WhatsApp think a real person is active"
            />
            <Stack gap={10}>
              <ToggleRow
                label="Show typing indicator"
                hint='Shows "typing..." bubble before each reply'
                value={s.typingIndicator} onChange={v => set("typingIndicator", v)}
              />
              <ToggleRow
                label="Simulate read receipts"
                hint="Marks messages as seen before replying"
                value={s.readReceiptDelay} onChange={v => set("readReceiptDelay", v)}
              />
            </Stack>
          </SafetyCard>

          {/* Message Batching */}
          <SafetyCard>
            <SectionHead
              icon="filter" color="violet"
              title="Message Batching"
              subtitle="Wait and combine rapid messages into one reply"
              toggle={s.batchingEnabled} onToggle={v => set("batchingEnabled", v)}
            />
            <SliderRow
              label="Batch window"
              hint="Wait after last message before replying once"
              value={s.batchWindow} min={1} max={15}
              onChange={v => set("batchWindow", v)}
              disabled={!s.batchingEnabled}
            />
            <div style={{
              padding: "10px 12px", borderRadius: 8, background: T.bg0,
              border: `1px solid ${T.border1}`, fontSize: 12, color: T.text3, lineHeight: 1.5, marginTop: 14,
            }}>
              <span style={{ color: T.violet, fontWeight: 600 }}>Example:</span> Someone sends 3 messages quickly → bot waits {s.batchWindow}s → reads all → replies once.
            </div>
          </SafetyCard>

          {/* Filler Detection */}
          <SafetyCard>
            <SectionHead
              icon="mic-off" color="indigo"
              title="Filler Detection"
              subtitle="Skip GPT only for exact acknowledgement words"
              toggle={s.fillerDetectionEnabled} onToggle={v => set("fillerDetectionEnabled", v)}
            />
            <div>
              <div style={{ fontSize: 12, color: T.text2, marginBottom: 9, fontWeight: 700 }}>Filler word list</div>
              <FillerChips
                words={s.fillerWords}
                onChange={v => set("fillerWords", v)}
                disabled={!s.fillerDetectionEnabled}
              />
            </div>
            <div style={{ marginTop: 14 }}>
              <div style={{ fontSize: 12, color: T.text2, marginBottom: 6, fontWeight: 700 }}>Auto-skip short messages (max word count)</div>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <input
                  type="number"
                  min={0}
                  max={10}
                  value={s.fillerMaxWordCount}
                  onChange={e => set("fillerMaxWordCount", Math.max(0, parseInt(e.target.value) || 0))}
                  disabled={!s.fillerDetectionEnabled}
                  style={{
                    width: 64, padding: "6px 10px", borderRadius: 8,
                    border: `1px solid ${T.border1}`, background: T.bg1,
                    color: T.text1, fontSize: 13, textAlign: "center",
                  }}
                />
                <span style={{ fontSize: 12, color: T.text3 }}>
                  {s.fillerMaxWordCount === 0
                    ? "Off — only exact filler words above are skipped"
                    : `Messages with ≤ ${s.fillerMaxWordCount} word${s.fillerMaxWordCount > 1 ? "s" : ""} are auto-skipped`}
                </span>
              </div>
            </div>
            {!s.fillerDetectionEnabled && (
              <WarnBox level="amber">Bot will respond to every message including "ok", "thanks", etc. Wastes AI credits.</WarnBox>
            )}
            {s.fillerDetectionEnabled && (
              <div style={{
                marginTop: 12, padding: "9px 12px", borderRadius: 8, fontSize: 11,
                color: T.text3, background: T.bg0, border: `1px solid ${T.border1}`, lineHeight: 1.6,
              }}>
                Only words in this list are skipped. Stretched spellings like okayyy also count as the same word.
              </div>
            )}
          </SafetyCard>

        </Stack>

        {/* ── RIGHT COLUMN ───────────────────────────────────────── */}
        <Stack>

          {/* Rate Limits */}
          <SafetyCard>
            <SectionHead
              icon="zap" color="amber"
              title="Rate Limits"
              subtitle="Cap messages per minute to avoid spam detection"
              toggle={s.rateLimitEnabled} onToggle={v => set("rateLimitEnabled", v)}
            />
            <Stack gap={17}>
              <SliderRow
                label="Per-contact limit"
                hint="Max replies to one person per minute"
                value={s.rateLimitPerContact} min={1} max={20} unit="/min"
                onChange={v => set("rateLimitPerContact", v)} disabled={!s.rateLimitEnabled}
              />
              <SliderRow
                label="Global limit"
                hint="Max total replies across all contacts per minute"
                value={s.rateLimitGlobal} min={5} max={100} unit="/min"
                onChange={v => set("rateLimitGlobal", v)} disabled={!s.rateLimitEnabled}
              />
              <SliderRow
                label="Daily cap per contact"
                hint="Max replies to one person per day (0 = unlimited)"
                value={s.dailyContactLimit} min={0} max={50} unit=" msgs"
                onChange={v => set("dailyContactLimit", v)}
              />
            </Stack>
            {s.rateLimitEnabled && s.rateLimitGlobal > 40 && (
              <WarnBox level="amber">Over 40 replies/min looks like automation. Keep it under 30.</WarnBox>
            )}
          </SafetyCard>

          {/* Flood Guard */}
          <SafetyCard>
            <SectionHead
              icon="shield" color="rose"
              title="Flood Guard"
              subtitle="Ignore rapid repeat messages from same contact"
            />
            <SliderRow
              label="Ignore re-messages within"
              hint="Skip messages from same contact sent within this window"
              value={s.floodGuardSeconds} min={0} max={30}
              onChange={v => set("floodGuardSeconds", v)}
            />
            {!s.floodGuardSeconds && (
              <WarnBox level="amber">Flood guard off — a single spammer can trigger unlimited AI calls.</WarnBox>
            )}
          </SafetyCard>

          {/* Broadcast Safety Delay */}
          <SafetyCard>
            <SectionHead
              icon="broadcast" color="teal"
              title="Broadcast Safety Delay"
              subtitle="Pause between each broadcast message"
            />
            <SliderRow
              label="Delay between messages"
              hint="±40% random jitter applied automatically"
              value={s.broadcastDelay} min={3} max={60}
              onChange={v => set("broadcastDelay", v)}
            />
            {s.broadcastDelay < 5 && (
              <WarnBox>Under 5s between broadcasts almost always causes bans.</WarnBox>
            )}
            <div style={{
              padding: "10px 12px", borderRadius: 8, background: T.bg0,
              border: `1px solid ${T.border1}`, fontSize: 12, color: T.text3, marginTop: 14, lineHeight: 1.5,
            }}>
              Actual interval: <strong style={{ color: T.text2 }}>{Math.round(s.broadcastDelay * 0.6)}–{Math.round(s.broadcastDelay * 1.4)}s</strong> with jitter. Recommend 8–20s minimum.
            </div>
          </SafetyCard>

          {/* Safe settings reference */}
          <SafetyCard style={{ background: T.emeraldDim, border: `1px solid rgba(99,102,241,0.18)` }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
              <Icon name="info" size={15} color={T.emerald} />
              <div style={{ fontSize: 13, fontWeight: 600, color: T.emerald }}>Recommended safe settings</div>
            </div>
            {[
              ["Reply delay",        "8–15 seconds"     ],
              ["Typing indicator",   "Always ON"        ],
              ["Read receipts",      "Always ON"        ],
              ["Batching",           "ON · 5–8s window" ],
              ["Per-contact rate",   "Max 5/min"        ],
              ["Global rate",        "Max 20–30/min"    ],
              ["Flood guard",        "3–5s window"      ],
              ["Filler detection",   "Always ON"        ],
              ["Broadcast delay",    "8–20s + jitter"   ],
            ].map(([k, v], i, arr) => (
              <div key={i} style={{
                display: "flex", justifyContent: "space-between", padding: "7px 0",
                borderBottom: i < arr.length - 1 ? "1px solid rgba(99,102,241,0.1)" : "none", fontSize: 12,
              }}>
                <span style={{ color: T.text2 }}>{k}</span>
                <span style={{ color: T.emerald, fontWeight: 600, fontFamily: base.mono }}>{v}</span>
              </div>
            ))}
          </SafetyCard>

        </Stack>
      </div>

      {/* Sticky save bar */}
      <div style={{
        position: "sticky", bottom: 0,
        background: `linear-gradient(to top,${T.bg0} 72%,transparent)`,
        padding: "18px 0 0", marginTop: 18,
        display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap",
      }}>
        <Btn variant="primary" size="lg" onClick={save} disabled={saving}>
          {saving
            ? <><div style={{ width: 14, height: 14, border: "2px solid rgba(0,0,0,0.2)", borderTopColor: "#fff", borderRadius: "50%", animation: "spin 0.7s linear infinite" }} /> Saving...</>
            : <><Icon name="shield" size={15} color="#fff" /> Save safety settings</>
          }
        </Btn>
        <button
          onClick={resetDefaults}
          style={{ background: "none", border: "none", cursor: "pointer", fontSize: 13, color: T.text3, fontFamily: base.font, transition: "color 0.15s" }}
          onMouseOver={e => (e.target.style.color = T.text2)}
          onMouseOut={e  => (e.target.style.color = T.text3)}
        >
          Reset to defaults
        </button>
        <div style={{ marginLeft: "auto", fontSize: 12, fontWeight: 600, color: riskColor, display: "flex", alignItems: "center", gap: 6 }}>
          <Icon name="shield" size={13} color={riskColor} />
          Risk score: {risk}/100
        </div>
      </div>
    </div>
  );
}
